import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Input, Select, Spin } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  GlobalOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  LockOutlined,
  MobileOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SaveOutlined,
  SendOutlined,
  ShopOutlined,
  TranslationOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";

import Alerts from "/src/util/alert";

import { LangUpdateContext } from "../../layout/LangContext";
import { MANAGER_PROFILE, PROFILE_PAGE } from "../../config/lang";
import { LANGS } from "../../config/languages";
import { call, callForm } from "../../util/action";
import { update_me } from "../../util/redux/actions";
import { translate } from "../../util/util";

import "./profile.scss";

/** Only these are editable; everything else on the record is read-only here. */
const EDITABLE = ["first_name", "last_name", "position", "telegram_id", "county", "language"];

const ROLE_KEY = {
  supervisor_manager: "role_supervisor_manager",
  supervisor: "role_supervisor",
};

/** A handle, with or without the leading @; the server is the authority beyond this. */
const isTelegram = v => !v || /^@?[A-Za-z0-9_]{5,32}$/.test(v.trim());

const initialsOf = me => ((me?.first_name?.[0] || "") + (me?.last_name?.[0] || "")).toUpperCase() || "?";

/** county may arrive as a full object or a bare id depending on how it was last written. */
const countyId = c => (c && typeof c === "object" ? c.id : c) ?? null;

function Avatar({ src, initials, large, onError }) {
  const cls = `pf-avatar${large ? " pf-avatar--lg" : ""}`;
  /* A stored photo whose file no longer exists is common on old records — this account's
     points at a 2021 upload that 404s. Without the fallback the page shows a broken image. */
  if (src) return <img className={`${cls} pf-avatar--img`} src={src} alt="" onError={onError} />;
  return (
    <span className={cls} aria-hidden="true">
      {initials}
    </span>
  );
}

/** A locked, non-editable value — the design's `control--ro`. */
function ReadOnlyField({ label, icon, value, hint }) {
  return (
    <div className="pf-field">
      <span className="pf-label">
        {label}
        <LockOutlined className="pf-lock" aria-hidden="true" />
      </span>
      <div className="pf-ro">
        {icon}
        <span>{value || "—"}</span>
      </div>
      {hint ? <span className="pf-hint">{hint}</span> : null}
    </div>
  );
}

/** Label with the design's trailing required asterisk. antd's own mark sits before the text. */
function Req({ children }) {
  return (
    <>
      {children}
      <span className="pf-req" aria-hidden="true">
        {" *"}
      </span>
    </>
  );
}

/** Tinted note box — the design's own palette rather than antd Alert's. */
function Note({ tone, icon, children }) {
  return (
    <p className={`pf-note${tone === "warn" ? " pf-note--warn" : ""}`}>
      {icon}
      <span>{children}</span>
    </p>
  );
}

function Section({ title, desc, children }) {
  return (
    <section className="pf-card">
      <div className="pf-card__head">
        <h2 className="pf-card__title">{title}</h2>
        {desc ? <p className="pf-card__desc">{desc}</p> : null}
      </div>
      {children}
    </section>
  );
}

export default function Profile() {
  const me = useSelector(state => state.me);
  const dispatch = useDispatch();
  const langCtx = React.useContext(LangUpdateContext);
  const [form] = Form.useForm();

  const [counties, setCounties] = useState([]);
  const [loadingCounties, setLoadingCounties] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  /** A staged photo: `File` to upload, `null` to clear, `undefined` to leave alone. */
  const [photo, setPhoto] = useState(undefined);
  const [preview, setPreview] = useState(null);
  const [imgFailed, setImgFailed] = useState(false);
  const fileRef = useRef(null);

  /* `me.language` drives the copy, and it is also an editable field — so labels follow the
   *saved* language, not the pending selection. Changing it needs a reload to take effect. */
  const lang = me?.language;
  const t = useCallback(key => translate(PROFILE_PAGE, key, lang), [lang]);
  const tp = useCallback(key => translate(MANAGER_PROFILE, key, lang), [lang]);

  const pristine = useMemo(() => {
    const out = {};
    for (const k of EDITABLE) out[k] = k === "county" ? countyId(me?.[k]) : me?.[k] ?? null;
    return out;
  }, [me]);

  useEffect(() => {
    form.setFieldsValue(pristine);
    setDirty(false);
    setPhoto(undefined);
    setPreview(null);
  }, [pristine, form]);

  /* The region list is small and closed, so one fetch beats a paged async select. */
  useEffect(() => {
    let alive = true;
    call({ method: "GET", url: "counties/?active=true" }, r => {
      if (!alive) return;
      setCounties(Array.isArray(r?.results) ? r.results : Array.isArray(r) ? r : []);
      setLoadingCounties(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  /* Object URLs are a leak if they are never revoked. */
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const recomputeDirty = useCallback(() => {
    const now = form.getFieldsValue(EDITABLE);
    const changed = EDITABLE.some(k => (now[k] ?? null) !== (pristine[k] ?? null));
    setDirty(changed || photo !== undefined);
  }, [form, pristine, photo]);

  useEffect(recomputeDirty, [photo, recomputeDirty]);

  const pickPhoto = file => {
    if (!file) return;
    setPhoto(file);
    setImgFailed(false);
    setPreview(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const discard = () => {
    form.setFieldsValue(pristine);
    setPhoto(undefined);
    setPreview(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setDirty(false);
  };

  const submit = values => {
    setSaving(true);
    const prevLanguage = me?.language;

    const done = r => {
      setSaving(false);
      if (!r || !r.id) {
        Alerts.warning("Error while saving. " + JSON.stringify(r), { position: "bottom-right" });
        return;
      }
      /* A photo change the server did not apply must not report success. `photo: ""` used to be
         dropped silently by DRF (HTTP 200, field untouched), so the page cheerfully said "saved"
         while the avatar stayed put — which is exactly how this looked like a UI bug. */
      if (photo !== undefined && (photo === null) === !!r.photo) {
        /* Deliberately no dispatch: updating `me` recomputes the pristine snapshot, which resets
           the form and throws away the staged photo — so a failed save would silently discard
           what the user asked for. Leaving it dirty keeps Save available for a retry. The other
           fields did persist; the local copy catches up on the next successful save or reload. */
        Alerts.warning("Error while saving. " + JSON.stringify({ photo: r.photo ?? null }), {
          position: "bottom-right",
        });
        return;
      }

      dispatch(update_me({ ...me, ...r }));
      if (r.language !== prevLanguage) {
        /* Almost every screen reads me.language at render time from persisted state, so a
           reload is still how a language change reaches the whole app. */
        Alerts.success(tp("save_success_reload"), { position: "bottom-right", timeout: 1000 });
        setTimeout(() => window.location.reload(), 250);
        return;
      }
      Alerts.success(tp("save_success"), { position: "bottom-right" });
      langCtx?.updateLangFn?.();
      setDirty(false);
      setPhoto(undefined);
    };

    /* PATCH, not PUT: only the fields on this form should ever be written, and a partial PUT
       would blank whatever the serializer accepts but this page does not show. */
    if (photo === undefined) {
      call({ method: "PATCH", url: `users/${me.id}/`, post: values }, done);
      return;
    }

    /* A photo change has to go as multipart. An empty string is how the API clears a FileField. */
    const fd = new FormData();
    for (const k of EDITABLE) {
      const v = values[k];
      if (v !== null && v !== undefined && v !== "") fd.append(k, v);
    }
    fd.append("photo", photo === null ? "" : photo);
    callForm({ method: "PATCH", url: `users/${me.id}/`, post: fd }, done);
  };

  const storedPhoto = photo === null ? null : preview || me?.photo || null;
  const photoSrc = imgFailed && !preview ? null : storedPhoto;
  /* Offered whenever a photo is *recorded*, even if its file is missing — clearing the stale
     reference is exactly what Remove is for. */
  const canRemove = storedPhoto !== null;
  const initials = initialsOf(me);
  const fullName = `${me?.first_name || ""} ${me?.last_name || ""}`.trim();
  const roleLabel = ROLE_KEY[me?.role] ? t(ROLE_KEY[me.role]) : me?.role || "";
  const languageChanged = Form.useWatch("language", form) !== pristine.language;
  const currentCounty = counties.find(c => c.id === countyId(me?.county));
  const currentLang = LANGS.find(l => l.code === lang);

  return (
    <div className="shell-content pf">
      <div className="pf-head">
        <h1 className="pf-title">{tp("my_profile")}</h1>
        <p className="pf-sub">{t("profile_sub")}</p>
      </div>

      <Form form={form} className="pf-grid" layout="vertical" requiredMark={false} onFinish={submit} onValuesChange={recomputeDirty} validateTrigger="onChange">
        <div className="pf-main">
          <Section title={t("sec_identity")} desc={t("sec_identity_desc")}>
            <div className="pf-avarow">
              <Avatar src={photoSrc} initials={initials} large onError={() => setImgFailed(true)} />
              <div className="pf-avaacts">
                <div className="pf-avabtns">
                  <Button icon={<UploadOutlined />} onClick={() => fileRef.current?.click()}>
                    {t("upload_photo")}
                  </Button>
                  {canRemove ? (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        setPhoto(null);
                        setPreview(null);
                      }}>
                      {t("remove_photo")}
                    </Button>
                  ) : null}
                  <input ref={fileRef} id="photo" name="photo" type="file" accept="image/*" hidden onChange={e => pickPhoto(e.target.files?.[0])} />
                </div>
                <span className="pf-hint">{t("photo_hint")}</span>
              </div>
            </div>

            <div className="pf-row">
              <Form.Item name="first_name" label={<Req>{tp("name")}</Req>} required rules={[{ required: true, whitespace: true, message: t("err_required") }]}>
                <Input size="large" autoComplete="given-name" />
              </Form.Item>
              <Form.Item
                name="last_name"
                label={<Req>{tp("surname")}</Req>}
                required
                rules={[{ required: true, whitespace: true, message: t("err_required") }]}>
                <Input size="large" autoComplete="family-name" />
              </Form.Item>
            </div>

            <Form.Item name="position" label={tp("position")} extra={t("position_hint")}>
              <Input size="large" autoComplete="organization-title" />
            </Form.Item>
          </Section>

          <Section title={t("sec_contact")} desc={t("sec_contact_desc")}>
            <ReadOnlyField label={t("phone")} icon={<MobileOutlined />} value={me?.phone_number} hint={t("phone_hint")} />
            <Form.Item
              name="telegram_id"
              label={tp("telegram")}
              extra={t("telegram_hint")}
              rules={[
                {
                  validator: (_, v) => (isTelegram(v) ? Promise.resolve() : Promise.reject(new Error(t("err_telegram")))),
                },
              ]}>
              <Input size="large" prefix={<SendOutlined />} placeholder="@username" />
            </Form.Item>
          </Section>

          <Section title={t("sec_work")} desc={t("sec_work_desc")}>
            <Form.Item name="county" label={tp("my_region")} extra={t("region_hint")}>
              <Select
                size="large"
                showSearch
                allowClear
                loading={loadingCounties}
                optionFilterProp="label"
                suffixIcon={loadingCounties ? <Spin size="small" /> : undefined}
                options={counties.map(c => ({ value: c.id, label: c.name }))}
              />
            </Form.Item>

            <div className="pf-row">
              <ReadOnlyField label={t("role")} icon={<SafetyCertificateOutlined />} value={roleLabel} />
              <ReadOnlyField label={t("company")} icon={<ShopOutlined />} value={me?.company?.name} />
            </div>
            <span className="pf-hint">{t("readonly_hint")}</span>
          </Section>

          <Section title={t("sec_prefs")} desc={t("sec_prefs_desc")}>
            <Form.Item name="language" label={tp("language")}>
              <Select
                size="large"
                options={LANGS.map(l => ({
                  value: l.code,
                  label: l.native ? `${l.label} · ${l.native}` : l.label,
                }))}
              />
            </Form.Item>
            {languageChanged ? (
              <Note tone="warn" icon={<ReloadOutlined />}>
                {t("language_reload_warn")}
              </Note>
            ) : null}
          </Section>
        </div>

        <aside className="pf-rail">
          <section className="pf-card pf-idcard">
            <Avatar src={photoSrc} initials={initials} large onError={() => setImgFailed(true)} />
            <div className="pf-idcard__name">{fullName || "—"}</div>
            {me?.position ? <div className="pf-idcard__pos">{me.position}</div> : null}
            {roleLabel ? (
              <span className="pf-badge">
                <IdcardOutlined />
                {roleLabel}
              </span>
            ) : null}
          </section>

          <section className="pf-card">
            <div className="pf-card__head">
              <h2 className="pf-card__title">{t("account")}</h2>
            </div>
            <ul className="pf-facts">
              <li>
                <MobileOutlined />
                <span className="pf-facts__k">{t("phone")}</span>
                <span className="pf-facts__v">{me?.phone_number || "—"}</span>
              </li>
              <li>
                <GlobalOutlined />
                <span className="pf-facts__k">{tp("my_region")}</span>
                <span className="pf-facts__v">{currentCounty?.name || "—"}</span>
              </li>
              <li>
                <TranslationOutlined />
                <span className="pf-facts__k">{tp("language")}</span>
                <span className="pf-facts__v">{currentLang?.label || lang || "—"}</span>
              </li>
              <li>
                <ShopOutlined />
                <span className="pf-facts__k">{t("company")}</span>
                <span className="pf-facts__v">{me?.company?.name || "—"}</span>
              </li>
            </ul>
          </section>

          <Note icon={<InfoCircleOutlined />}>{t("rail_note")}</Note>
        </aside>

        {/* Only present while there is something to save — nothing to ignore the rest of the time. */}
        {dirty ? (
          <div className="pf-savebar">
            <span className="pf-savebar__msg">
              <EditOutlined />
              {t("unsaved_changes")}
            </span>
            <span className="pf-savebar__acts">
              <Button type="text" onClick={discard} disabled={saving}>
                {t("discard")}
              </Button>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
                {tp("save")}
              </Button>
            </span>
          </div>
        ) : null}
      </Form>
    </div>
  );
}
